import { create } from 'zustand';
import { AsyncState } from './types';
import { API_BASE_URL } from '@/config/constants';

interface WalletContact {
  contactId: number;
  displayName: string;
  companyName?: string;
  jobTitle?: string;
  emails?: Array<{ email: string; type: string; isPrimary?: boolean }>;
  phones?: Array<{ number: string; type: string; isPrimary?: boolean }>;
  isFavorite: boolean;
  sourceType: 'qr_scan' | 'physical_card';
  createdAt: string;
  category?: string;
  tags?: string;
}

interface WalletState extends AsyncState {
  contacts: WalletContact[];
  totalContacts: number;
  isLoaded: boolean;

  fetchContacts: (accessToken: string, forceRefresh?: boolean) => Promise<void>;
  reset: () => void;
}

const initialState = {
  contacts: [],
  totalContacts: 0,
  isLoaded: false,
  loading: false,
  error: null,
};

export const useWalletStore = create<WalletState>((set, get) => ({
  ...initialState,

  fetchContacts: async (accessToken: string, forceRefresh = false) => {
    const { isLoaded, loading } = get();
    if (isLoaded && !loading && !forceRefresh) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/wallet`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load contacts');
      }

      const data = await response.json();
      const contacts = data.data?.contacts || data.contacts || [];
      const totalContacts = data.data?.totalContacts || data.totalContacts || contacts.length;

      set({
        contacts,
        totalContacts,
        isLoaded: true,
        loading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contacts';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  reset: () => set(initialState),
}));
